import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/pages/AuthPage";
import { api, formatErr } from "@/lib/api";
import { Lock, Copy, Check, ShieldCheck } from "lucide-react";

export default function TikTokWelcomePage() {
  const { user, refresh, loaded } = useAuth();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!loaded || !user) {
    return (
      <div
        dir="rtl"
        className="min-h-screen bg-black flex items-center justify-center text-gold text-[11px] tracking-[0.35em] font-mono-ar animate-pulse"
      >
        جاري تحميل بيانات الحساب…
      </div>
    );
  }

  const agentId = user.agent_id || "";
  const hasPassword = Boolean(user.has_password);

  const handleCopyAgentId = async () => {
    if (!agentId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(agentId));
      } else {
        const ta = document.createElement("textarea");
        ta.value = String(agentId);
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!newPassword || newPassword.length < 4) {
      setError("كلمة المرور قصيرة جداً (4 أحرف على الأقل)");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("كلمتا المرور غير متطابقتين");
      return;
    }

    setBusy(true);
    try {
      await api.post("/users/me/password", {
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      if (typeof refresh === "function") {
        await refresh();
      }
      navigate("/app", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(formatErr(detail) || "فشل تعيين كلمة المرور");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="relative min-h-screen w-full overflow-hidden text-white bg-black flex items-center justify-center p-4"
      data-testid="tiktok-welcome-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: "url('/auth/login-bg.png')" }}
        aria-hidden
      />

      <div className="relative z-10 w-[92%] max-w-[440px]">
        <div
          style={{
            background: "linear-gradient(180deg, rgba(14,14,14,0.94) 0%, rgba(6,6,6,0.96) 100%)",
            border: "1px solid rgba(197,160,89,0.45)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.85), inset 0 0 1px rgba(197,160,89,0.35)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            padding: "24px 22px",
            borderRadius: "2px",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#3a3a3a]">
            <ShieldCheck className="text-gold" size={20} />
            <div>
              <h1 className="text-[14px] font-cairo text-white font-bold tracking-wider">
                مرحباً بك في وحدة التحقيق OK FBI
              </h1>
              <p className="text-[11px] font-cairo text-zinc-400">
                تم ربط حساب TikTok وإنشاء ملف المحقق بنجاح
              </p>
            </div>
          </div>

          {/* Agent ID Callout */}
          <div
            className="mb-5 p-3.5 bg-black/70 border border-[#c5a059]/50 flex flex-col items-center justify-center text-center"
            style={{ borderRadius: "2px" }}
          >
            <span className="text-[11px] font-cairo text-zinc-300 mb-1">
              «رقم المحقق الخاص بك»
            </span>
            <div className="flex items-center gap-3 my-1">
              <span
                data-testid="welcome-agent-id"
                className="text-2xl font-mono-ar font-bold text-gold tracking-[0.25em]"
              >
                {agentId}
              </span>
              <button
                type="button"
                onClick={handleCopyAgentId}
                title="نسخ رقم المحقق"
                className="p-1.5 border border-[#c5a059]/40 hover:border-[#c5a059] bg-[#c5a059]/10 hover:bg-[#c5a059]/20 text-gold transition-colors"
                style={{ borderRadius: "2px" }}
              >
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
            <p className="text-[10px] font-cairo text-zinc-400 mt-1 max-w-[280px]">
              احتفظ بهذا الرقم بعناية، فهو اسم المستخدم الذي ستستخدمه لتسجيل الدخول لاحقاً.
            </p>
          </div>

          {/* Password Setup Form or Already Set */}
          {hasPassword ? (
            <div className="space-y-3">
              <div
                className="p-2.5 bg-emerald-950/30 border border-emerald-600/40 text-emerald-300 text-[11px] font-cairo text-center"
                style={{ borderRadius: "2px" }}
              >
                تم تعيين كلمة المرور لحسابك بالفعل.
              </div>
              <button
                type="button"
                onClick={() => navigate("/app", { replace: true })}
                className="w-full py-2.5 text-[13px] font-cairo tracking-[0.25em]"
                style={{
                  background: "linear-gradient(180deg, #d9b060 0%, #a4813f 100%)",
                  color: "#0a0a0a",
                  border: "1px solid #d9b060",
                  boxShadow: "0 6px 22px rgba(197,160,89,0.35)",
                  borderRadius: "2px",
                }}
              >
                متابعة إلى لوحة التحقيق
              </button>
            </div>
          ) : (
            <form onSubmit={handleSetPassword}>
              <div className="text-[11px] font-cairo text-zinc-300 mb-2">
                اختر كلمة مرور لحسابك لتتمكن من تسجيل الدخول برقم المحقق:
              </div>

              <label className="block text-[11px] text-zinc-300 font-cairo mb-1">
                كلمة المرور الجديدة
              </label>
              <div className="flex items-center gap-2 border border-[#3a3a3a] focus-within:border-[#c5a059] bg-black/60 px-2 py-2 mb-3">
                <Lock size={13} className="text-zinc-500" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="welcome-password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="w-full bg-transparent outline-none text-white text-[13px] font-mono-ar tracking-wider"
                />
              </div>

              <label className="block text-[11px] text-zinc-300 font-cairo mb-1">
                تأكيد كلمة المرور
              </label>
              <div className="flex items-center gap-2 border border-[#3a3a3a] focus-within:border-[#c5a059] bg-black/60 px-2 py-2 mb-3">
                <Lock size={13} className="text-zinc-500" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="welcome-password-confirm"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="w-full bg-transparent outline-none text-white text-[13px] font-mono-ar tracking-wider"
                />
              </div>

              {error && (
                <div
                  className="mb-3 text-[11px] text-red-300 font-cairo bg-red-950/40 p-2 border border-red-800/50"
                  style={{ borderRadius: "2px" }}
                  data-testid="welcome-password-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !newPassword || !confirmPassword}
                data-testid="welcome-password-submit"
                className="w-full mt-2 py-2.5 text-[13px] font-cairo tracking-[0.25em] disabled:opacity-50 transition-all"
                style={{
                  background: busy
                    ? "#8a7038"
                    : "linear-gradient(180deg, #d9b060 0%, #a4813f 100%)",
                  color: "#0a0a0a",
                  border: "1px solid #d9b060",
                  boxShadow: "0 6px 22px rgba(197,160,89,0.35)",
                  borderRadius: "2px",
                }}
              >
                {busy ? "جاري الحفظ…" : "حفظ كلمة المرور والدخول"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
